import { useState, useSyncExternalStore } from 'react';
import { Plus } from '../../icons';
import {
  TASK_STATUSES, TASK_STATUS_META,
  type TaskDto, type TaskStatus,
} from '../../api/tasks';
import { TaskStatusIcon } from '../../components/shared';
import { TaskCard } from './TaskCard';
// 2026-08-26 — 컬럼 헤더 테두리에 겹쳐 그리는 비중·지연 미터. 신규 파일(카드 영역 무영향).
import { ColumnMeter, isOverdue } from './task-viz';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';

interface KanbanBoardProps {
  tasks: TaskDto[];
  loading: boolean;
  onStatusChange: (taskId: string, newStatus: TaskStatus) => void | Promise<void>;
  onAddInColumn: (status: TaskStatus) => void;
  /** v2.7.2 — 카드 클릭 → 상세 레이어팝업 (미전달 시 기존 동작 무변경). */
  onOpenTask?: (task: TaskDto) => void;
  /** 카드의 담당자 이름 표시용 팀원 목록 (2026-08-31) — 그대로 TaskCard 로 넘긴다. */
  members?: { userId: string; name: string }[];
  /**
   * 카드 제목 옆 [🗑] (2026-09-02). 미전달 시 버튼이 아예 안 뜬다(기존 동작 무변경).
   * 지울 수 있는 카드인지의 판정은 TaskPage 가 하고, 여기서는 그 함수를 카드로 흘려보낸다.
   */
  onDeleteTask?: (task: TaskDto) => void;
  /** 이 카드를 지울 수 있는가 — 주지 않으면 모든 카드에 [🗑] 이 뜬다. */
  canDeleteTask?: (task: TaskDto) => boolean;
}

/**
 * KanbanBoard (P3-TASK-FE-3) — 4 컬럼 (시안 line 1883~1888 매핑).
 *
 * Drag-drop: 네이티브 HTML5 — TaskCard 가 draggable, KanbanColumn 이 dropzone.
 * drop 시 onStatusChange(taskId, newStatus) → optimistic patchTask 호출.
 *
 * 후속: column 안 sortOrder 정렬 drag-drop (현재 status 변경만).
 */
export function KanbanBoard({
  tasks, loading, onStatusChange, onAddInColumn, onOpenTask, members, onDeleteTask, canDeleteTask,
}: KanbanBoardProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<TaskStatus | null>(null);

  const tasksByStatus: Record<TaskStatus, TaskDto[]> = {
    TODO: [], IN_PROGRESS: [], REVIEW: [], DONE: [],
  };
  for (const t of tasks) {
    if (t.status in tasksByStatus) {
      tasksByStatus[t.status as TaskStatus].push(t);
    }
  }
  // sortOrder ASC + id ASC.
  for (const arr of Object.values(tasksByStatus)) {
    arr.sort((a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : 1));
  }

  /*
   * 컬럼 미터의 기준 (2026-08-26) — 막대 길이는 **가장 큰 컬럼**을 100% 로 잡는다.
   * 전체 합을 기준으로 하면 4칸에 고르게 퍼진 프로젝트에서 네 막대가 다 짧아져
   * 아무것도 구분되지 않는다. 비교하고 싶은 것은 '칸 사이의 차이' 라서 최댓값 기준이 맞다.
   */
  const columnMax = Math.max(...TASK_STATUSES.map((s) => tasksByStatus[s].length), 0);
  const overdueByStatus = TASK_STATUSES.reduce<Record<TaskStatus, number>>((acc, s) => {
    acc[s] = tasksByStatus[s].filter(isOverdue).length;
    return acc;
  }, { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 });

  function handleDragStart(e: React.DragEvent, taskId: string) {
    setDraggingId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }
  function handleDragEnd() {
    setDraggingId(null);
    setOverColumn(null);
  }

  function handleDragOver(e: React.DragEvent, status: TaskStatus) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overColumn !== status) setOverColumn(status);
  }
  function handleDragLeave(e: React.DragEvent, status: TaskStatus) {
    // dataTransfer 가 column 안 자식 이동 시에도 dragleave 발생 — relatedTarget 검사로 false-positive 방지.
    const rel = e.relatedTarget as Node | null;
    if (rel && (e.currentTarget as Node).contains(rel)) return;
    if (overColumn === status) setOverColumn(null);
  }

  function handleDrop(e: React.DragEvent, status: TaskStatus) {
    e.preventDefault();
    setOverColumn(null);
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    if (!id) return;
    const task = tasks.find((t) => t.id === id);
    if (!task || task.status === status) return;
    void onStatusChange(id, status);
  }

  return (
    <div className="task-kanban" aria-busy={loading}>
      {TASK_STATUSES.map((status) => {
        const meta = TASK_STATUS_META[status];
        const columnTasks = tasksByStatus[status];
        return (
          <div
            key={status}
            className={`task-kanban-column ${overColumn === status ? 'task-kanban-column--over' : ''}`}
            onDragOver={(e) => handleDragOver(e, status)}
            onDragLeave={(e) => handleDragLeave(e, status)}
            onDrop={(e) => handleDrop(e, status)}
            role="region"
            aria-label={meta.label}
          >
            <header className="task-kanban-column-header">
              <span className="task-kanban-column-title" style={{ color: meta.color }}>
                <span className="task-kanban-column-emoji" aria-hidden="true"><TaskStatusIcon status={status} size={14} /></span>
                {meta.label}
              </span>
              <span className="task-kanban-column-count">{columnTasks.length}</span>
              {/* 지연이 있을 때만 — 없을 때 '지연 0' 을 적으면 네 칸 모두에 잡음이 깔린다. */}
              {overdueByStatus[status] > 0 && (
                <span className="task-kanban-column-over">
                  {tTask('task.kanban.column_overdue', { n: String(overdueByStatus[status]) })}
                </span>
              )}
              <button
                type="button"
                className="task-kanban-column-add"
                onClick={() => onAddInColumn(status)}
                aria-label={tTask('task.kanban.add_in_column_aria', { column: meta.label })}
              >
                <Plus size={12} />
              </button>
              {/* 헤더 테두리 위에 겹쳐 그린다 — 카드가 보이는 높이는 종전 그대로. */}
              <ColumnMeter
                status={status}
                count={columnTasks.length}
                overdue={overdueByStatus[status]}
                max={columnMax}
              />
            </header>
            <div className="task-kanban-column-body">
              {columnTasks.length === 0 && (
                <div className="task-kanban-column-empty">{tTask('task.kanban.empty_column')}</div>
              )}
              {columnTasks.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  dragging={draggingId === t.id}
                  onDragStart={(e) => handleDragStart(e, t.id)}
                  onDragEnd={handleDragEnd}
                  onOpen={onOpenTask ? () => onOpenTask(t) : undefined}
                  members={members}
                  onDelete={onDeleteTask && (canDeleteTask?.(t) ?? true) ? () => onDeleteTask(t) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
